import { onMounted, watchEffect } from 'vue'
import { useNuxtApp, useRuntimeConfig, useItemListContextStore, usePromotionStore, useUser, useUcConsentManager, useState } from '#imports'
import { useGtm } from '@gtm-support/vue-gtm'
import type { Schemas } from '#shopware'
import { useSessionContext } from '@shopware/composables'

type SearchHitProduct = Schemas['Product'] & {
    objectID?: string
    __position?: number
    __queryID?: string
    absoluteIndex?: number
}

type EcommerceItem = SearchHitProduct | Schemas['LineItem'] | Schemas['OrderLineItem']

type AdditionalItemData = {
    qty?: number
    index?: number | string
    listId?: string
    listName?: string
    categoryId?: string
    categoryName?: string
}

export function useGtmTracking() {
    const $gtm = useGtm()
    const nuxtApp = useNuxtApp()
    const config = useRuntimeConfig().public
    const { sessionContext } = useSessionContext()
    const { user, isLoggedIn, isGuestSession } = useUser()
    const gtmUserType = useState<'logged_in' | 'guest'>('gtm-user-type', () => 'guest')
    const gtmUserId = useState<string | undefined>('gtm-user-id', () => undefined)
    watchEffect(() => {
        gtmUserType.value = isLoggedIn.value && !isGuestSession.value ? 'logged_in' : 'guest'
        gtmUserId.value = (sessionContext.value as { extensions?: { emarsys?: { externalId?: string } } })?.extensions?.emarsys?.externalId
    })
    const { promotionData } = usePromotionStore()
    const { itemListContextData } = useItemListContextStore()
    const { onUcIsInitialized, getUcServiceStatus, onUcConsentChange } = useUcConsentManager()

    // Track first page view for view_item and view_item_list events
    const hasTrackedItemView = useState<boolean>('gtm-has-tracked-item-view', () => false)

    const affiliation: string | false = typeof config.gtmEcommerceAffiliation === 'string' && config.gtmEcommerceAffiliation !== ''
        ? config.gtmEcommerceAffiliation
        : false

    function getCurrencyIso(): string {
        return sessionContext.value?.currency?.isoCode ?? 'EUR'
    }

    function clearEcommerce() {
        const w = window as { dataLayer?: unknown[] }
        w.dataLayer ??= []
        w.dataLayer.push({ ecommerce: null, payload: null })
    }

    function getShippingTotal(cart: Schemas['Cart']): number {
        return cart.deliveries?.reduce((sum, d) => sum + (d.shippingCosts?.totalPrice ?? 0), 0) ?? 0
    }

    function mapEcommerceItem(item: EcommerceItem, additionalData?: AdditionalItemData) {
        let sku: string | undefined
        let name: string | undefined
        let objectId: string | undefined
        let brandName: string | undefined
        let price: number | undefined
        let listPrice: number | undefined
        let taxRate: number | undefined
        // GTM tracking fields (promotion, item list) stored in the Shopware payload at cart-time.
        // Only LineItem and OrderLineItem carry a payload with these fields; Products do not.
        let lineItemPayload: Schemas['LineItemGtmPayload'] | undefined
        let payloadPromotion: Partial<Schemas['PromotionData']> | null = null

        // Two-level type discrimination based on top-level shape differences in the Shopware API schema.
        // Level 1: `productNumber` is a required top-level field on Product but only lives inside
        //   `.payload` on OrderLineItem and LineItem, so it uniquely identifies the Product branch.
        // Level 2 (price only): OrderLineItem exposes `unitPrice` directly at the top level; LineItem
        //   nests it inside a `price` object. All other fields (payload, label, id) are shared.
        if ('productNumber' in item) {
            // Schemas['Product'] / SearchHitProduct — no cart payload; lineItemPayload and payloadPromotion stay at their defaults
            const p = item as SearchHitProduct
            sku = p.productNumber
            name = p.translated?.name ?? p.name ?? undefined
            objectId = p.objectID ?? p.id ?? undefined
            brandName = p.manufacturer?.translated?.name ?? p.manufacturer?.name ?? undefined
            price = p.calculatedPrice?.unitPrice
            listPrice = p.calculatedPrice?.listPrice?.price ?? undefined
            taxRate = p.calculatedPrice?.taxRules?.[0]?.taxRate
        } else {
            // Schemas['OrderLineItem'] | Schemas['LineItem'] — payload, label, id and GTM promotion fields are shared
            const li = item as Schemas['OrderLineItem'] | Schemas['LineItem']
            lineItemPayload = li.payload as Schemas['LineItemGtmPayload'] | undefined
            sku = li.payload?.productNumber
            name = li.label ?? undefined
            objectId = li.id ?? undefined
            // Extract manufacturer from payload (LineItems store product data including manufacturer in payload)
            const payloadManufacturer = (li.payload as { manufacturer?: Schemas['ProductManufacturer'] })?.manufacturer
            brandName = payloadManufacturer?.translated?.name ?? payloadManufacturer?.name ?? undefined

            const { promotion_id, promotion_name, creative_name, creative_slot } = lineItemPayload ?? {}
            const partialPromotion: Partial<Schemas['PromotionData']> = {
                ...(typeof promotion_id === 'string' && promotion_id.trim() !== '' && { promotion_id }),
                ...(typeof promotion_name === 'string' && promotion_name.trim() !== '' && { promotion_name }),
                ...(typeof creative_name === 'string' && creative_name.trim() !== '' && { creative_name }),
                ...(typeof creative_slot === 'string' && creative_slot.trim() !== '' && { creative_slot }),
            }
            if (Object.keys(partialPromotion).length > 0) {
                payloadPromotion = partialPromotion
            }

            // Only price shape differs: OrderLineItem has unitPrice at the top level, LineItem nests it inside price
            if ('unitPrice' in item && !('price' in item && item.price != null)) {
                // Schemas['OrderLineItem'] — secondary `price` check is a safety net: treat as LineItem if both keys are present
                const orderLineItem = item as Schemas['OrderLineItem']
                price = orderLineItem.unitPrice
                // OrderLineItem stores taxRate in priceDefinition.taxRules (products) or root taxRules (promotions)
                const priceDefTaxRate = (orderLineItem as { priceDefinition?: { taxRules?: Array<{ taxRate?: number }> } }).priceDefinition?.taxRules?.[0]?.taxRate
                const rootTaxRate = (orderLineItem as { taxRules?: Array<{ taxRate?: number }> }).taxRules?.[0]?.taxRate
                taxRate = priceDefTaxRate ?? rootTaxRate
            } else {
                // Schemas['LineItem']
                const lineItem = item as Schemas['LineItem']
                price = lineItem.price?.unitPrice
                listPrice = lineItem.price?.listPrice?.price
                taxRate = lineItem.price?.taxRules?.[0]?.taxRate
            }
        }

        const discount = listPrice != null && price != null && listPrice > price ? listPrice - price : undefined
        const effectivePromotion: Partial<Schemas['PromotionData']> | null = payloadPromotion ?? promotionData.value ?? null

        // Item list context — priority: payload (LineItem/OrderLineItem) > additionalData > context store
        let finalListId: string | undefined
        let finalListName: string | undefined
        let finalIndex: number | string = additionalData?.index ?? 0

        if (lineItemPayload?.item_list_id != null) {
            finalListId = lineItemPayload.item_list_id
            finalListName = lineItemPayload.item_list_name
            finalIndex = lineItemPayload.index ?? additionalData?.index ?? 0
        } else if (additionalData?.listId != null || additionalData?.listName != null) {
            finalListId = additionalData.listId
            finalListName = additionalData.listName
        } else if (itemListContextData.value != null) {
            finalListId = itemListContextData.value.item_list_id
            finalListName = itemListContextData.value.item_list_name
            finalIndex = additionalData?.index ?? itemListContextData.value.index ?? 0
        }

        return {
            item_id: sku,
            item_name: name,
            object_id: objectId,
            ...(affiliation && { affiliation }),
            ...(discount != null && { discount }),
            index: finalIndex,
            ...(brandName != null && { item_brand: brandName }),
            ...(finalListId != null && { item_list_id: finalListId }),
            ...(finalListName != null && { item_list_name: finalListName }),
            price,
            quantity: additionalData?.qty ?? 1,
            ...(taxRate != null && { taxRate }),
            ...(additionalData?.categoryId != null && { item_category_id: additionalData.categoryId }),
            ...(additionalData?.categoryName != null && { item_category: additionalData.categoryName }),
            ...(effectivePromotion != null && { ...effectivePromotion }),
        }
    }

    /**
     * Cart events
     */
    nuxtApp.hook('addToCart', ({ addQty, product, action }) => {
        clearEcommerce()
        const unitPrice = 'productNumber' in product
            ? product.calculatedPrice?.unitPrice
            : product.price?.unitPrice
        $gtm?.trackEvent({
            event: 'add_to_cart',
            action: 'click',
            ecommerce: {
                currency: getCurrencyIso(),
                value: (unitPrice ?? 0) * addQty,
                items: [mapEcommerceItem(product, { qty: addQty })],
                ...(action != null && { action }),
            },
        })
    })

    nuxtApp.hook('removeFromCart', ({ product, qty, action }) => {
        const computedQty = qty ?? product.quantity ?? 1
        clearEcommerce()
        $gtm?.trackEvent({
            event: 'remove_from_cart',
            action: 'click',
            ecommerce: {
                currency: getCurrencyIso(),
                value: (product.price?.unitPrice ?? 0) * computedQty,
                items: [mapEcommerceItem(product, { qty: computedQty })],
                ...(action != null && { action }),
            },
        })
    })

    nuxtApp.hook('viewCart', ({ cart }) => {
        clearEcommerce()
        $gtm?.trackEvent({
            event: 'view_cart',
            ecommerce: {
                currency: getCurrencyIso(),
                value: cart.price?.totalPrice,
                items: (cart.lineItems ?? []).map((li, index) =>
                    mapEcommerceItem(li, { index, qty: li.quantity ?? 1 })
                ),
            },
        })
    })

    nuxtApp.hook('openAddedCartLayer', ({ cart, recentlyAddedLineItem }) => {
        clearEcommerce()
        $gtm?.trackEvent({
            event: 'open_added_cart_layer',
            ecommerce: {
                cart_total: cart.price?.totalPrice,
                currency: getCurrencyIso(),
                value: recentlyAddedLineItem?.price?.totalPrice ?? 0,
                shipping: getShippingTotal(cart),
                tax: cart.price?.calculatedTaxes?.reduce((sum, t) => sum + (t.tax ?? 0), 0) ?? 0,
                items: recentlyAddedLineItem != null
                    ? [mapEcommerceItem(recentlyAddedLineItem, { index: 0, qty: recentlyAddedLineItem.quantity ?? 1 })]
                    : [],
            },
        })
    })

    nuxtApp.hook('closeAddedCartLayer', ({ action, cart, recentlyAddedLineItem }) => {
        clearEcommerce()
        $gtm?.trackEvent({
            event: 'close_added_cart_layer',
            action,
            ecommerce: {
                cart_total: cart.price?.totalPrice,
                currency: getCurrencyIso(),
                value: recentlyAddedLineItem?.price?.totalPrice ?? 0,
                shipping: getShippingTotal(cart),
                tax: cart.price?.calculatedTaxes?.reduce((sum, t) => sum + (t.tax ?? 0), 0) ?? 0,
                items: recentlyAddedLineItem != null
                    ? [mapEcommerceItem(recentlyAddedLineItem, { index: 0, qty: recentlyAddedLineItem.quantity ?? 1 })]
                    : [],
            },
        })
    })

    nuxtApp.hook('goToBasket', ({ cart, recentlyAddedLineItem }) => {
        clearEcommerce()
        $gtm?.trackEvent({
            event: 'go_to_basket',
            ecommerce: {
                cart_total: cart.price?.totalPrice,
                currency: getCurrencyIso(),
                value: recentlyAddedLineItem?.price?.totalPrice ?? 0,
                shipping: getShippingTotal(cart),
                tax: cart.price?.calculatedTaxes?.reduce((sum, t) => sum + (t.tax ?? 0), 0) ?? 0,
                items: recentlyAddedLineItem != null
                    ? [mapEcommerceItem(recentlyAddedLineItem, { index: 0, qty: recentlyAddedLineItem.quantity ?? 1 })]
                    : [],
            },
        })
    })

    nuxtApp.hook('openRecos', ({ cart, recentlyAddedLineItem }) => {
        clearEcommerce()
        $gtm?.trackEvent({
            event: 'open_recos',
            ecommerce: {
                cart_total: cart.price?.totalPrice,
                currency: getCurrencyIso(),
                value: recentlyAddedLineItem?.price?.totalPrice ?? 0,
                shipping: getShippingTotal(cart),
                tax: cart.price?.calculatedTaxes?.reduce((sum, t) => sum + (t.tax ?? 0), 0) ?? 0,
                items: recentlyAddedLineItem != null
                    ? [mapEcommerceItem(recentlyAddedLineItem, { index: 0, qty: recentlyAddedLineItem.quantity ?? 1 })]
                    : [],
            },
        })
    })

    /**
     * Product listing events
     */
    nuxtApp.hook('viewProductListing', ({ id, name, products }) => {
        const isFirstPageView = !hasTrackedItemView.value
        if (isFirstPageView) {
            hasTrackedItemView.value = true
        }
        clearEcommerce()
        $gtm?.trackEvent({
            event: 'view_item_list',
            first_page_view: isFirstPageView,
            ecommerce: {
                item_list_id: id,
                item_list_name: name,
                items: products.map((product, index) => {
                    const itemIndex = (product as SearchHitProduct).absoluteIndex ?? index
                    return mapEcommerceItem(product, { index: itemIndex, listId: id, listName: name })
                }),
            },
        })
    })

    nuxtApp.hook('clickProductListingCard', ({ id, name, product, index }) => {
        clearEcommerce()
        $gtm?.trackEvent({
            event: 'select_item',
            ecommerce: {
                item_list_id: id,
                item_list_name: name,
                items: [mapEcommerceItem(product, { index, listId: id, listName: name })],
            },
        })
    })

    /**
     * Product detail events
     */
    nuxtApp.hook('viewProductDetail', ({ product, breadcrumb }) => {
        const isFirstPageView = !hasTrackedItemView.value
        if (isFirstPageView) {
            hasTrackedItemView.value = true
        }
        const lastCrumb = breadcrumb[breadcrumb.length - 1]
        const categoryId = lastCrumb != null && 'categoryId' in lastCrumb ? lastCrumb.categoryId : undefined
        clearEcommerce()
        $gtm?.trackEvent({
            event: 'view_item',
            first_page_view: isFirstPageView,
            ecommerce: {
                currency: getCurrencyIso(),
                value: product.calculatedPrice?.unitPrice,
                items: [mapEcommerceItem(product, {
                    ...(lastCrumb?.name != null && { categoryName: lastCrumb.name }),
                    ...(categoryId != null && { categoryId }),
                })],
            },
        })
    })

    nuxtApp.hook('productVariantChanged', ({ data: product }) => {
        clearEcommerce()
        $gtm?.trackEvent({
            event: 'change_variant',
            ecommerce: {
                currency: getCurrencyIso(),
                value: product.calculatedPrice?.unitPrice,
                items: [mapEcommerceItem(product)],
            },
        })
    })

    /**
     * Checkout events
     */
    nuxtApp.hook('viewCheckout', ({ cart }) => {
        clearEcommerce()
        $gtm?.trackEvent({
            event: 'begin_checkout',
            ecommerce: {
                currency: getCurrencyIso(),
                value: cart?.price?.totalPrice,
                items: (cart?.lineItems ?? []).map((li, index) =>
                    mapEcommerceItem(li, { index, qty: li.quantity ?? 1 })
                ),
            },
        })
    })

    nuxtApp.hook('submitContactInfo', ({ shippingTier, cart }) => {
        clearEcommerce()
        $gtm?.trackEvent({
            event: 'add_contact_info',
            ecommerce: {
                currency: getCurrencyIso(),
                value: cart?.price?.totalPrice,
                shipping_tier: shippingTier,
                items: (cart?.lineItems ?? []).map((li, index) =>
                    mapEcommerceItem(li, { index, qty: li.quantity ?? 1 })
                ),
            },
        })
    })

    nuxtApp.hook('selectCheckoutStep', ({ step }) => {
        $gtm?.trackEvent({ event: 'checkout_funnel', step })
    })

    nuxtApp.hook('selectPaymentMethod', ({ payment, cart }) => {
        if (payment == null) return
        clearEcommerce()
        $gtm?.trackEvent({
            event: 'add_payment_info',
            ecommerce: {
                currency: getCurrencyIso(),
                value: cart?.price?.totalPrice,
                payment_type: payment.translated?.name ?? payment.name,
                items: (cart?.lineItems ?? []).map((li, index) =>
                    mapEcommerceItem(li, { index, qty: li.quantity ?? 1 })
                ),
            },
        })
    })

    nuxtApp.hook('placeOrder', ({ order }) => {
        if (order == null) return

        clearEcommerce()

        const coupons = (order.lineItems ?? [])
            .filter(li => li.type === 'promotion')
            .map(li => (li.payload as { code?: string })?.code ?? li.label)
            .filter((code): code is string => code != null && code.trim() !== '')

        const billingAddress = order.addresses?.find(a => a.id === order.billingAddressId)
        const consumerCountry = billingAddress?.country?.iso
            ?? config.platformCountries.find(c => c.id === billingAddress?.countryId)?.iso

        const totalTax = order.price?.calculatedTaxes?.reduce((sum, t) => sum + (t.tax ?? 0), 0)

        $gtm?.trackEvent({
            event: 'purchase',
            ecommerce: {
                email: order.orderCustomer?.email,
                currency: getCurrencyIso(),
                value: order.price?.totalPrice,
                transaction_id: order.orderNumber,
                shipping: order.shippingCosts?.totalPrice,
                tax: totalTax,
                items: (order.lineItems ?? []).map((li, index) =>
                    mapEcommerceItem(li, { index, qty: li.quantity })
                ),
                ...(coupons.length > 0 && { coupon: coupons }),
                ...(user.value?.status != null && { customerType: user.value.status }),
                consumerSalutation: user.value?.salutation?.salutationKey,
                consumerFirstName: user.value?.firstName,
                consumerLastName: user.value?.lastName,
                consumerCountry,
                consumerZipcode: billingAddress?.zipcode,
                consumerStreet: billingAddress?.street,
                consumerYearOfBirth: user.value?.birthday,
                payment_type: order.transactions?.at(-1)?.paymentMethod?.translated?.name ?? order.transactions?.at(-1)?.paymentMethod?.name,
                shipping_tier: order.deliveries?.at(-1)?.shippingMethod?.translated?.name ?? order.deliveries?.at(-1)?.shippingMethod?.name,
            },
        })
    })

    /**
     * Auth events
     */
    nuxtApp.hook('login', () => {
        clearEcommerce()
        $gtm?.trackEvent({ event: 'login' })
    })

    nuxtApp.hook('register', () => {
        clearEcommerce()
        $gtm?.trackEvent({ event: 'sign_up' })
    })

    /**
     * Search events
     */
    nuxtApp.hook('searchApiAutocompleteView', ({ payload }) => {
        clearEcommerce()
        $gtm?.trackEvent({
            event: 'search_api_autocomplete_view',
            payload,
        })
    })

    nuxtApp.hook('searchApiSearchView', ({ payload }) => {
        clearEcommerce()
        $gtm?.trackEvent({
            event: 'search_api_search_view',
            payload,
        })
    })

    /**
     * Promotion events
     */
    nuxtApp.hook('viewPromotion', ({ payload }) => {
        clearEcommerce()
        $gtm?.trackEvent({
            event: 'view_promotion',
            payload,
        })
    })

    nuxtApp.hook('selectPromotion', ({ payload, click_type, click_text }) => {
        clearEcommerce()
        $gtm?.trackEvent({
            event: 'select_promotion',
            ...(click_type != null && { click_type }),
            ...(click_text != null && { click_text }),
            payload,
        })
    })

    /**
     * Popup events
     */
    nuxtApp.hook('popupViewed', ({ popup, pageType, triggerEvent, sessionPageCount }) => {
        clearEcommerce()
        $gtm?.trackEvent({
            event: 'popup_viewed',
            category: 'popup',
            action: 'view',
            label: popup.type,
            popup_id: popup.id,
            popup_name: popup.name,
            popup_layout: popup.layout,
            page_type: pageType,
            trigger_event: triggerEvent,
            popup_headline: popup.translated?.headline,
            popup_has_media: popup.media != null,
            popup_viewports: popup.viewports?.join(','),
            popup_customer_groups: popup.customergroups?.join(','),
            ...(sessionPageCount != null && { session_page_count: sessionPageCount }),
            ...(popup.delay != null && { popup_delay: popup.delay }),
            ...(popup.voucher != null && { popup_has_voucher: true }),
        })
    })

    nuxtApp.hook('popupClicked', ({ popup, pageType, click_text }) => {
        clearEcommerce()
        $gtm?.trackEvent({
            event: 'popup_clicked',
            category: 'popup',
            action: 'click',
            label: popup.type,
            popup_id: popup.id,
            popup_name: popup.name,
            page_type: pageType,
            ...(click_text != null && { click_text }),
        })
    })

    nuxtApp.hook('popupClosed', ({ popup, closeMethod, pageType, timeOnPopup }) => {
        clearEcommerce()
        $gtm?.trackEvent({
            event: 'popup_closed',
            category: 'popup',
            action: 'close',
            label: `${popup.type}_${closeMethod}`,
            popup_id: popup.id,
            popup_name: popup.name,
            page_type: pageType,
            ...(timeOnPopup != null && { time_on_popup_seconds: Math.round(timeOnPopup / 1000) }),
        })
    })

    /**
     * Helper function to push consent update to gtag
     */
    function updateGtagConsent(hasConsent: boolean) {
        const w = window as { dataLayer?: unknown[] }
        w.dataLayer ??= []

        // Push consent update event to dataLayer
        w.dataLayer.push(['consent', 'update', {
            ad_storage: hasConsent ? 'granted' : 'denied',
            ad_user_data: hasConsent ? 'granted' : 'denied',
            ad_personalization: hasConsent ? 'granted' : 'denied',
            analytics_storage: hasConsent ? 'granted' : 'denied',
        }])
    }

    /**
     * Consent management — must run client-side after window is available
     */
    onMounted(() => {
        const ucServiceName = 'Google Tag Manager'
        let lastConsentState: boolean | null = null

        onUcIsInitialized(() => {
            const hasConsent = getUcServiceStatus(ucServiceName)
            lastConsentState = hasConsent

            // Enable/disable GTM based on initial consent state
            // Consent update is sent only when user actively changes consent
            if (hasConsent && !$gtm?.enabled()) {
                $gtm?.enable(true)
            } else if (!hasConsent && $gtm?.enabled()) {
                $gtm?.enable(false)
            }

            onUcConsentChange((e) => {
                if (e.detail && e.detail.event === 'consent_status') {
                    const newConsent = e.detail[ucServiceName] === true

                    // Only send update if consent actually changed (prevent duplicate events)
                    if (newConsent !== lastConsentState) {
                        lastConsentState = newConsent

                        // Send consent update when user changes their choice
                        updateGtagConsent(newConsent)

                        if (newConsent && !$gtm?.enabled()) {
                            $gtm?.enable(true)
                        } else if (!newConsent && $gtm?.enabled()) {
                            $gtm?.enable(false)
                            location.reload()
                        }
                    }
                }
            })
        })
    })
}
